import { ChangeDetectorRef, Component, HostListener, inject } from '@angular/core';
import {
  NavigationError,
  RouteConfigLoadEnd,
  RouteConfigLoadStart,
  Router,
  RouterModule,
} from '@angular/router';
import { filter } from 'rxjs/operators';
import { CommonService } from './services/common.service';
import { SpinnerOverlayService } from './shared/spinner/spinner-overlay.service';
import { OrderOrigin } from './models/enum';
import { configureChunkRecovery, isChunkLoadError, reloadForChunkError } from './services/chunk-reload';
import { AppUpdateService } from './services/app-update.service';
import { TermlyConsentService } from './services/termly-consent.service';

import { SpinnerOverlayComponent } from './shared/spinner/spinner-overlay.component';
import { ToastComponent } from './shared/toast/toast.component';
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [RouterModule, SpinnerOverlayComponent, ToastComponent],
})
export class AppComponent {
  private readonly commonService = inject(CommonService);
  private readonly router = inject(Router);
  private readonly spinner = inject(SpinnerOverlayService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly appUpdateService = inject(AppUpdateService);
  private readonly termlyConsentService = inject(TermlyConsentService);

  @HostListener('window:message', ['$event'])
  onWindowMessage(event: MessageEvent): void {
    if (event?.data && event.data.consent !== undefined) {
      this.termlyConsentService.setConsentState(event.data.consent);
      try {
        localStorage.setItem(
          'termly_consent',
          JSON.stringify(event.data.consent)
        );
      } catch (err) {
        console.warn('[termly] localStorage save error:', err);
      }
    }
  }

  // MP-5600. Much of the embed's state lives in memory (cart, order, the
  // CommonService ids signup and checkout read) or comes only from the URL
  // it booted with (smtxorigin, iFrameId, hideScroll, isStaffUser, webview
  // flags). A full page load mid-flow loses it, so the embed does NOT move
  // open tabs onto a new build at the next navigation: its sessions are short,
  // and old builds' files are kept after a deploy. Forced updates (breaking
  // deploys) still apply, but never mid-purchase or inside the attendee /
  // Box Office app webviews; chunk recovery keeps the entry query string.
  private readonly initializeAppUpdate = (() => {
    configureChunkRecovery({
      preserveEntryQuery: true,
      allowUnroutedRecovery: () => !this.isPurchaseInProgress(window.location.href),
    });
    this.appUpdateService.start({
      updateOnNavigation: false,
      skipFullLoad: url => this.isPurchaseInProgress(url),
    });
  })();

  private isPurchaseInProgress(url: string): boolean {
    return (
      /\/(checkout|ordercomplete|signup|timeout|questions|membership-questions|attendee-document|exchange|edit-card-detail|switch-time|organizer-square-giftcard|order-center|password-domain-privacy)(\/|\?|#|$)/i.test(
        new URL(url, window.location.origin).pathname
      ) ||
      !!this.commonService.orderIdentifier ||
      this.commonService.isOpenSuperbFromAttendeeAPP ||
      this.commonService.isOpenSuperbFromBoxOfficeAPP
    );
  }

  // Initialize smtxorigin handling
  private readonly initializeSmtxOrigin = (() => {
    const url = window.location.href;
    if (url.includes('?') && url.split('?').length > 0) {
      const urlParam = url.split('?')[1].split('&');
      if (urlParam.length > 0 && urlParam[0]) {
        const urltoken = urlParam.filter(
          r => r.split('=')[0] && r.split('=')[0] === 'smtxorigin'
        );
        if (
          urltoken &&
          urltoken.length > 0 &&
          urltoken[0].split('=')[0] == 'smtxorigin'
        ) {
          var smtxorigin = urltoken[0].split('=')[1];
          if (smtxorigin.includes('#'))
            this.commonService.originType = smtxorigin.split('#')[0];
          else this.commonService.originType = smtxorigin;

          if (
            this.commonService.originType &&
            parseInt(this.commonService.originType) ===
              OrderOrigin.DirectV3Embed
          ) {
            this.setSingleScrollMode(urlParam);
            // Get the iFrameId From the queryParams
            const iFrameUrl = new URL(url);
            this.commonService.iframeId =
              iFrameUrl.searchParams.get('iFrameId') || '';
          }
        }
        this.getStaffUser(urlParam);
      }
    }
  })();

  // Initialize router events handling
  private readonly initializeRouterEvents = (() => {
    this.router.events
      .pipe(
        filter(
          event =>
            event instanceof RouteConfigLoadStart ||
            event instanceof RouteConfigLoadEnd ||
            event instanceof NavigationError
        )
      )
      .subscribe(event => {
        if (event) {
          if (event instanceof RouteConfigLoadStart) {
            this.loadRouterSpinner(event, true);
          } else if (event instanceof RouteConfigLoadEnd) {
            this.loadRouterSpinner(event, false);
            this.removeVersionChangeFromURL();
          }
          if (event instanceof NavigationError) {
            // RouteConfigLoadEnd never comes for a failed chunk: dismiss the
            // checkout/ordercomplete spinner so a suppressed reload doesn't
            // leave the page covered.
            this.spinner.stopSpinning();
            if (isChunkLoadError(event.error)) {
              // Signup only works with the ids CommonService holds in memory, which a page load loses: reload the page the buyer is on (checkout) instead.
              const toSignup = /^\/signup(\/|\?|#|$)/.test(new URL(event.url, window.location.origin).pathname);
              reloadForChunkError(event.error, toSignup ? undefined : event.url);
              this.changeDetectorRef.detectChanges();
            }
          }
        }
      });
  })();

  private setSingleScrollMode(urlParam: string[]) {
    if (
      urlParam &&
      urlParam.length > 1 &&
      urlParam[1] &&
      urlParam[1].split('=') &&
      urlParam[1].split('=').length === 2 &&
      urlParam[1].split('=')[0] == 'hideScroll'
    ) {
      if (urlParam[1].split('=')[1] === 'true') {
        this.commonService.singleScrollMode = true;
      } else {
        this.commonService.singleScrollMode = false;
      }
    }
  }

  private loadRouterSpinner(event: any, isLoadSpinner: boolean): void {
    if (
      event.route &&
      event.route.path &&
      (event.route.path.includes('checkout') ||
        event.route.path.includes('ordercomplete'))
    ) {
      isLoadSpinner
        ? this.spinner.startSpinning()
        : this.spinner.stopSpinning();
    }
  }

  private removeVersionChangeFromURL(): void {
    const location = new URL(window.location.href);
    const params = new URLSearchParams(location.search);
    if (params.has('v')) {
      params.delete('v');
      location.search = params.toString();
      window.history.replaceState(window.history.state, document.title, location.toString());
    }
  }

  private getStaffUser(urlParam: string[]): void {
    try {
      const isStaffUser = urlParam.filter(
        r => r?.split('=')[0] && r?.split('=')[0] === 'isStaffUser'
      );
      if (
        isStaffUser?.length > 0 &&
        isStaffUser[0]?.split('=')[0] == 'isStaffUser'
      ) {
        this.commonService.isStaffUser = Boolean(isStaffUser[0]?.split('=')[1]);
      }
    } catch (error) {
      console.error(error);
    }
  }
}
